import clsx from 'clsx';

import './button.css';

import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';

import { IconLoader2 } from '@tabler/icons-react';

interface ICustomButton extends ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  icon?: React.ReactNode;
  isRightIcon?: boolean;
  variant?: 'primary' | 'outline' | 'unstyled';
  desructive?: boolean;
  href?: string;
  dataTestId?: string;
  loading?: boolean;
}

interface ICustomLink extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant: 'link';
  href?: string;
  label?: string;
  icon?: React.ReactNode;
  isRightIcon?: boolean;
  desructive?: boolean;
  loading?: boolean;
  dataTestId?: string;
}

type TButton = ICustomButton | ICustomLink;

const btnDestructive = 'btn-destructive';

const getButtonVariant = (variant?: string): string | undefined => {
  if (variant === 'unstyled') return undefined;
  if (variant === 'outline') return 'btn-outline';
  return 'btn-primary';
};

const Button = forwardRef<HTMLButtonElement, TButton>(
  (
    {
      label,
      type = 'button',
      onClick,
      icon,
      disabled,
      isRightIcon = false,
      className,
      variant,
      desructive,
      href,
      dataTestId = 'button',
      loading = false,
      ...rest
    },
    ref
  ) => {
    if (variant === 'link') {
      const isActuallyDisabled = disabled || loading;
      const LinkComponent = (
        isActuallyDisabled ? 'div' : Link
      ) as React.ElementType;

      if (href)
        return (
          <LinkComponent
            className={clsx(
              'btn-link',
              desructive && btnDestructive,
              isActuallyDisabled && 'disabled',
              className
            )}
            to={href}
            role={isActuallyDisabled ? 'link' : undefined}
            aria-disabled={isActuallyDisabled}
            data-testid={dataTestId}
            ref={ref}
            {...rest}
          >
            {!isRightIcon && icon}
            {label}
            {isRightIcon && icon}
          </LinkComponent>
        );

      return (
        <button
          className={clsx(
            'btn-link',
            desructive && btnDestructive,
            isActuallyDisabled && 'disabled',
            className
          )}
          onClick={onClick}
          type={type}
          disabled={isActuallyDisabled}
          data-testid={dataTestId}
          ref={ref}
          {...rest}
        >
          {!isRightIcon && icon}
          {label}
          {isRightIcon && icon}
        </button>
      );
    }

    const buttonVariant = getButtonVariant(variant);

    return (
      <button
        className={clsx(buttonVariant, desructive && btnDestructive, className)}
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        data-testid={dataTestId}
        ref={ref}
        {...rest}
      >
        {loading ? (
          <IconLoader2 className="animate-spin h-5 w-5 text-white" />
        ) : (
          <>
            {!isRightIcon && icon}
            {label}
            {isRightIcon && icon}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
